import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Asset,
  AssetFilterInputObject,
  FilterGroupingEnum,
} from 'graphql-common';
import Table from '@lib/components/Table/Table';
import Search from '@lib/components/Search/Search';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import { IconButtonTypes } from '@lib/components/IconButton/IconButton';
import { TableAction } from '@lib/components/Table/types';
import useAssetsQueryHook from 'utils/fetch/hooks/useAssetsQueryHook';
import { APP_URLS } from 'constants/urls';
import useTabListState from '@lib/components/Table/utils/useTabListState';
import getEquipmentColumns from './utils/getEquipmentColumns';

const SEARCH_FILTER_SCHEMA = [
  'name',
  'code',
  'siteName',
  'siteCode',
  'siteAreaName',
  'siteAreaCode',
  'categoryName',
];

interface EquipmentTabProps {
  documentId: string;
}

export default function EquipmentTab({ documentId }: EquipmentTabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { listState, params, searchQuery, onSearchChange } = useTabListState();

  const additionalFilters: AssetFilterInputObject[] = useMemo(
    () => [
      {
        documentationsId: {
          grouping: FilterGroupingEnum.And,
          predicate: { eq: documentId },
          filterGroup: 'documentationsId',
        },
      },
    ],
    [documentId],
  );

  const assetsQueryHookResult = useAssetsQueryHook({
    additionalFilters,
    includeCategoryData: true,
    paginationParams: params,
    orderingParams: params,
    searchQuery,
    searchQueryFilterSchema: SEARCH_FILTER_SCHEMA,
  });

  const onViewHandler = useCallback(
    (item?: Asset) => {
      if (item?.id) {
        navigate(
          APP_URLS.dashboard.assets.view.getDynamicPath({
            pathParts: { id: `${item.id}` },
          }),
        );
      }
    },
    [navigate],
  );

  const columns = useMemo(() => getEquipmentColumns({ t }), [t]);

  const tableActions: TableAction<Asset>[] = useMemo(
    () => [
      {
        id: 'view-action',
        icon: 'visibility',
        onClick: (item: Asset) => onViewHandler(item),
        type: IconButtonTypes.PrimaryOutlined,
        predictorFnTooltipProps: () => ({ body: t('view') }),
      },
    ],
    [onViewHandler, t],
  );

  if (assetsQueryHookResult.firstLoading) {
    return <CircleLoader />;
  }

  const emptyState = (
    <ListEmptyState
      title={t('no-linked-equipment')}
      text={t('no-linked-equipment-text')}
    />
  );

  return (
    <>
      {((!!assetsQueryHookResult.totalCount &&
        !!assetsQueryHookResult.collection.length) ||
        searchQuery) && (
        <Search
          placeholder={t('document-equipment-search-placeholder')}
          value={searchQuery}
          onChange={onSearchChange}
          useSearchParam={false}
        />
      )}
      <Table
        actions={tableActions}
        columns={columns}
        counterText={t('asset-plural', {
          count: assetsQueryHookResult.totalCount,
        })}
        data={assetsQueryHookResult.collection}
        emptyState={emptyState}
        id="documentEquipment"
        loading={assetsQueryHookResult.loading}
        orderingState={listState}
        paginationState={listState}
        totalEntries={assetsQueryHookResult.totalCount}
      />
    </>
  );
}
